import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RevenueTrendChartProps {
  data?: Array<{
    date: string;
    amount: number;
  }>;
  loading: boolean;
  className?: string;
}

export default function RevenueTrendChart({ data, loading, className }: RevenueTrendChartProps) {
  if (loading) {
    return (
      <Card className={cn("athlyn-card", className)}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Revenue Trend</span>
            <TrendingUp className="w-5 h-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const chartData = data || [];
  
  // Format the chart data
  const formattedData = chartData.map(item => ({
    ...item,
    month: new Date(item.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    formattedAmount: item.amount / 1000 // Convert to thousands
  }));

  return (
    <Card className={cn("athlyn-card", className)}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Revenue Trend</span>
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {formattedData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No revenue data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart 
              data={formattedData} 
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                className="stroke-muted" 
              />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => `${value}k`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
                formatter={(value: number) => [`AED ${value.toFixed(1)}k`, 'Revenue']}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '20px' 
                }}
              />
              <Line 
                type="monotone" 
                dataKey="formattedAmount" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="Revenue (AED thousands)"
                dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}